import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const lifecycleSteps = [
  {
    step: '01',
    title: 'Asset Origination & Legal Wrapping',
    icon: 'ri-file-text-line',
    description: 'We work with your legal counsel to structure the real estate asset using Token and Title deed with a clear legal framework. Title deed verification, valuation reports, and jurisdiction-specific compliance checks are completed before any token is minted.',
    details: ['Token and Title deed registration in compliant jurisdiction', 'Third-party property valuation', 'Title deed audit & escrow', 'Investor term sheet preparation'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20legal%20document%20structuring%20for%20real%20estate%20tokenization%2C%20a%20building%20with%20floating%20legal%20papers%20and%20document%20icons%20around%20it%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20stylized%20artwork%2C%20fintech%20legal%20technology%20aesthetic%2C%20professional%20illustration%20for%20blockchain%20real%20estate%20platform&width=1000&height=700&seq=rwa-step01&orientation=landscape',
  },
  {
    step: '02',
    title: 'Token Design & Smart Contract',
    icon: 'ri-code-box-line',
    description: 'Our platform generates ERC-1400 / ERC-3643 compliant security token contracts tailored to your asset. We define token supply, fractional unit price, dividend distribution rules, transfer restrictions, and whitelist logic — all audited by third-party security firms.',
    details: ['ERC-1400 / ERC-3643 security token standard', 'Automated dividend & distribution logic', 'Transfer restriction & whitelist enforcement', 'Third-party smart contract audit report'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20smart%20contract%20code%20generation%20for%20blockchain%20real%20estate%20tokenization%2C%20floating%20code%20blocks%20and%20security%20lock%20icons%20around%20a%20building%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20artwork%2C%20professional%20blockchain%20development%20aesthetic&width=1000&height=700&seq=rwa-step02&orientation=landscape',
  },
  {
    step: '03',
    title: 'KYC / AML & Investor Onboarding',
    icon: 'ri-user-star-line',
    description: 'Every investor undergoes identity verification, KYC/AML screening, and accreditation checks before they are whitelisted. Our integrated compliance engine supports 190+ jurisdictions with automated PEP, sanctions, and adverse media screening powered by leading data providers.',
    details: ['Multi-jurisdiction KYC/AML screening', 'Automated PEP & sanctions checks', 'Accredited investor verification', 'Ongoing monitoring & re-screening'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20investor%20identity%20verification%20and%20KYC%20onboarding%20process%2C%20floating%20ID%20cards%20and%20shield%20check%20icons%20around%20user%20profiles%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20compliance%20artwork%2C%20professional%20regulatory%20technology%20aesthetic&width=1000&height=700&seq=rwa-step03&orientation=landscape',
  },
  {
    step: '04',
    title: 'Primary Issuance & Capital Raise',
    icon: 'ri-funds-line',
    description: 'Launch your token sale with configurable phases — private sale, pre-sale, and public offering. Our platform handles cap table management in real time, accepts fiat and stablecoin payments, and auto-distributes tokens upon settlement.',
    details: ['Multi-phase sale configuration', 'Fiat & stablecoin payment rails', 'Real-time cap table management', 'Automated token distribution on settlement'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20primary%20token%20issuance%20and%20capital%20raise%20for%20real%20estate%2C%20floating%20coins%20and%20investment%20charts%20around%20a%20building%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20fundraising%20artwork%2C%20professional%20investment%20platform%20aesthetic&width=1000&height=700&seq=rwa-step04&orientation=landscape',
  },
  {
    step: '05',
    title: 'Secondary Marketplace & Liquidity',
    icon: 'ri-store-2-line',
    description: 'After the primary raise, tokens are listed on our regulated secondary marketplace where whitelisted investors can trade. Our matching engine supports limit orders, atomic settlement via smart contracts, and real-time price discovery — all within the compliant perimeter.',
    details: ['Order book with limit & market orders', 'Atomic on-chain settlement', 'Real-time price discovery', 'Automated compliance at trade level'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20secondary%20marketplace%20trading%20for%20tokenized%20real%20estate%20assets%2C%20floating%20trading%20charts%20and%20exchange%20interface%20around%20a%20building%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20exchange%20artwork%2C%20professional%20marketplace%20platform%20aesthetic&width=1000&height=700&seq=rwa-step05&orientation=landscape',
  },
  {
    step: '06',
    title: 'Ongoing Compliance & Reporting',
    icon: 'ri-scales-3-line',
    description: 'Regulatory obligations do not end at issuance. Our platform generates automated reports for investors and regulators — including NAV updates, distribution statements, tax forms, and audit trails. Dashboards for issuers, investors, and regulators ensure full transparency.',
    details: ['Automated investor & regulator reporting', 'NAV updates & distribution statements', 'Full audit trail with immutable logs', 'Dedicated regulator dashboard & API'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20ongoing%20compliance%20reporting%20and%20regulatory%20dashboards%20for%20tokenized%20real%20estate%2C%20floating%20reports%20and%20audit%20logs%20around%20a%20building%20with%20shield%20icons%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20compliance%20artwork%2C%20professional%20regulatory%20technology%20aesthetic&width=1000&height=700&seq=rwa-step06&orientation=landscape',
  },
];

const platformFeatures = [
  { icon: 'ri-building-4-line', title: 'Multi-Asset Support', desc: 'Residential, commercial, industrial, and land — tokenize any real estate asset class on one platform.' },
  { icon: 'ri-global-line', title: '190+ Jurisdictions', desc: 'KYC/AML compliance engine covers virtually every jurisdiction with localized rule engines.' },
  { icon: 'ri-bank-line', title: 'Fiat & Crypto Rails', desc: 'Accept investments in USD, EUR, USDC, USDT, and major cryptocurrencies with automatic conversion.' },
  { icon: 'ri-shield-check-line', title: 'Regulated Framework', desc: 'Built on EU DLT Pilot Regime and MiCA-aligned architecture. Jurisdiction-specific adaptations available.' },
  { icon: 'ri-pie-chart-2-line', title: 'Cap Table Management', desc: 'Real-time cap table with investor breakdowns, vesting schedules, and dilution modeling.' },
  { icon: 'ri-file-chart-line', title: 'Automated Reporting', desc: 'NAV updates, distribution summaries, tax statements, and regulator reports — scheduled and on-demand.' },
];

const caseStudies = [
  {
    title: 'Luxury Residential Complex — Dubai',
    value: '$24M',
    metric: 'Tokenized in 8 weeks',
    description: 'A 42-unit luxury residential tower in Dubai Marina was tokenized into 240,000 security tokens at $100/token. The primary sale was oversubscribed in 12 days, and the secondary market now sees $1.2M in average weekly volume.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20a%20luxury%20residential%20tower%20in%20Dubai%20with%20floating%20token%20icons%20and%20investment%20charts%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20real%20estate%20artwork%2C%20professional%20case%20study%20illustration&width=800&height=600&seq=rwa-case1&orientation=landscape',
  },
  {
    title: 'Commercial Office Park — Frankfurt',
    value: '$41M',
    metric: '30-day issuance cycle',
    description: 'A Grade-A office park in Frankfurt\'s financial district was fractionalized, enabling 180 institutional and accredited investors to participate. Automated dividend distributions are processed quarterly via smart contract.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20a%20commercial%20office%20park%20in%20Frankfurt%20Germany%20with%20floating%20token%20icons%20and%20dividend%20charts%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20commercial%20real%20estate%20artwork%2C%20professional%20case%20study%20illustration&width=800&height=600&seq=rwa-case2&orientation=landscape',
  },
  {
    title: 'Industrial Portfolio — Singapore',
    value: '$18M',
    metric: 'Full lifecycle in 60 days',
    description: 'A portfolio of 3 logistics warehouses near Changi was tokenized end-to-end — from title deed registration to secondary trading — in under 60 days. The platform\'s automated compliance engine handled MAS regulatory requirements seamlessly.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20isometric%20illustration%20of%20industrial%20logistics%20warehouses%20in%20Singapore%20with%20floating%20token%20icons%20and%20compliance%20checkmarks%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20warm%20cream%20background%2C%20clean%20minimalist%20fintech%20industrial%20real%20estate%20artwork%2C%20professional%20case%20study%20illustration&width=800&height=600&seq=rwa-case3&orientation=landscape',
  },
];

export default function RWAPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    document.title = 'Real Estate Tokenization Platform — Issuance to Marketplace | Sky Edge Infotech';
    const metaTags = [
      { attr: 'name', name: 'description', content: 'Sky Edge Infotech RWA Tokenization — a turnkey platform for real estate asset tokenization covering the full lifecycle from legal structuring and token design through KYC/AML, primary issuance, secondary marketplace, and ongoing compliance. Built on ERC-3643 for 190+ jurisdictions.' },
      { attr: 'name', name: 'keywords', content: 'real estate tokenization, RWA tokenization platform, asset tokenization, ERC-3643, security token platform, real world assets, property tokenization, blockchain real estate, tokenized real estate marketplace, Sky Edge Infotech RWA' },
    ];
    const added: HTMLMetaElement[] = [];
    metaTags.forEach(({ attr, name, content }) => {
      const el = document.createElement('meta');
      el.setAttribute(attr, name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      added.push(el);
    });
    return () => { added.forEach((el) => el.remove()); };
  }, []);

  const handleInquirySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('.field-alt-website') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: 'Thank you! Our team will be in touch shortly.' });
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9ihrcmc26n1c7c5ptl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: 'Inquiry received! Our RWA product team will reach out within 24 hours.' });
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please email tokenization@skyedge.am.';
        setFormStatus({ type: 'error', message: msg });
      }
    } catch {
      setFormStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center bg-primary-950 overflow-hidden pt-16">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/60 to-primary-950" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-accent-500/8 blur-[150px]" />
        <div className="absolute bottom-20 right-20 w-[400px] h-[400px] rounded-full bg-primary-500/12 blur-[100px]" />

        <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="flex flex-col">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse"></span>
                In-house product
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-background-50 leading-tight">
                Real estate tokenization,
                <br />
                <span className="text-gradient-brand">issuance to marketplace.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-background-200 leading-relaxed max-w-xl">
                A turnkey platform for real-world asset tokenization — from legal structuring and smart contract deployment through KYC/AML, primary issuance, and secondary trading. Purpose-built for real estate, regulatory-first by design.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#inquiry-form" className="px-6 py-3.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center">
                  Talk to the RWA team <i className="ri-arrow-right-line"></i>
                </a>
                <a href="#lifecycle" className="px-6 py-3.5 rounded-md bg-background-50/8 hover:bg-background-50/15 border border-background-50/20 text-background-100 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center">
                  <i className="ri-arrow-down-line"></i>
                  See the lifecycle
                </a>
              </div>

              <div className="mt-10 flex items-center gap-8">
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">190+</span>
                  <span className="text-xs text-background-300">jurisdictions covered</span>
                </div>
                <div className="w-px h-10 bg-background-50/15" />
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">$83M+</span>
                  <span className="text-xs text-background-300">assets tokenized</span>
                </div>
                <div className="w-px h-10 bg-background-50/15" />
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">ERC-3643</span>
                  <span className="text-xs text-background-300">security token standard</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-lg overflow-hidden border border-background-50/15 bg-background-50/5 backdrop-blur">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20blockchain%20tokenization%20platform%20dashboard%20interface%20with%20clear%20crisp%20data%20panels%20showing%20real%20estate%20asset%20portfolio%20overview%2C%20token%20issuance%20metrics%2C%20cap%20table%20distribution%20charts%2C%20compliance%20status%20indicators%2C%20KYC%20verification%20progress%20bars%2C%20secondary%20market%20trading%20volume%20graphs%2C%20orange%20and%20navy%20blue%20accent%20highlights%20on%20a%20dark%20background%2C%20enterprise%20regtech%20dashboard%20design%20inspired%20by%20SettleMint%20platform%2C%20sharp%20readable%20typography%2C%20high%20contrast%20professional%20screenshot%20style%20with%20no%20blur%2C%20ultra%20detailed%20and%20clear%20UI%20elements&width=1200&height=900&seq=rwa-hero-dash-v2&orientation=landscape&nocache=true"
                  alt="Real estate tokenization platform illustration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-3 -left-3 hidden lg:flex items-center gap-3 bg-background-50 text-foreground-900 px-4 py-3 rounded-md border border-background-200">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-building-4-line"></i>
                </div>
                <div className="text-xs">
                  <div className="font-semibold">Issuance → Marketplace</div>
                  <div className="text-foreground-500">Full lifecycle · Built-in compliance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features Grid */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-apps-2-line"></i>
              Platform capabilities
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              Everything you need, in one regulated platform.
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              From title deed verification to secondary trading, every piece of the RWA tokenization puzzle is built in — no third-party stitching required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {platformFeatures.map((f) => (
              <div key={f.title} className="p-6 rounded-md border border-background-200 bg-background-50 hover:border-accent-300 transition-colors cursor-default">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-4">
                  <i className={`${f.icon} text-lg`}></i>
                </div>
                <div className="text-base font-semibold text-foreground-900 mb-2">{f.title}</div>
                <p className="text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section id="lifecycle" className="py-20 md:py-28 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-route-line"></i>
              End-to-end lifecycle
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              Issuance to marketplace — six stages, one platform.
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              Each stage is purpose-built with regulatory compliance baked in. No middleware. No external orchestrators. Just a clean, auditable pipeline from asset to trade.
            </p>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-10 md:left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block" />

            <div className="space-y-16">
              {lifecycleSteps.map((step, idx) => (
                <div key={step.step} className={`relative flex flex-col md:flex-row gap-8 md:gap-16 items-start ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  {/* Step marker */}
                  <div className="relative z-10 flex-shrink-0 hidden md:flex items-center justify-center w-20 h-20 rounded-full bg-accent-500 text-background-50 md:absolute md:left-1/2 md:-translate-x-1/2">
                    <span className="font-heading font-bold text-xl">{step.step}</span>
                  </div>

                  {/* Content */}
                  <div className={`w-full md:w-[calc(50%-60px)] ${idx % 2 === 1 ? 'md:pr-0' : 'md:pl-0'} ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                    <div className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 mb-3">
                      <span className="font-heading font-bold text-sm">{step.step}</span>
                    </div>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-3 ${idx % 2 === 0 ? 'md:ml-auto' : ''}`}>
                      <i className={`${step.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-3">{step.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{step.description}</p>
                    <ul className={`space-y-1.5 ${idx % 2 === 0 ? 'md:text-right' : ''}`}>
                      {step.details.map((d) => (
                        <li key={d} className={`flex items-center gap-2 text-xs text-foreground-500 ${idx % 2 === 0 ? 'md:justify-end' : ''}`}>
                          <span className="w-4 h-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                            <i className="ri-check-line text-[10px]"></i>
                          </span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Image */}
                  <div className={`w-full md:w-[calc(50%-60px)] ${idx % 2 === 1 ? 'md:pr-10' : 'md:pl-10'}`}>
                    <div className="rounded-md overflow-hidden border border-background-200">
                      <img
                        src={step.imageUrl}
                        alt={`${step.title} illustration`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-flashlight-line"></i>
              Case studies
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              Real assets, real results.
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              Our platform has tokenized over $83M in real estate assets across three continents. Here are a few highlights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((cs) => (
              <div key={cs.title} className="rounded-md border border-background-200 bg-background-50 overflow-hidden flex flex-col">
                <div className="relative">
                  <img src={cs.imageUrl} alt={cs.title} className="w-full h-48 object-cover object-top" />
                  <div className="absolute top-3 right-3 bg-accent-500 text-background-50 px-3 py-1 rounded-full text-xs font-semibold">
                    {cs.value}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-xs font-semibold text-accent-600 uppercase tracking-wider mb-1">{cs.metric}</div>
                  <h4 className="font-heading font-bold text-lg text-foreground-900 mb-2">{cs.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">{cs.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Deep-Dive */}
      <section className="py-20 md:py-28 bg-primary-950 text-background-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern opacity-25" />
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-primary-500/10 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-accent-500/8 blur-[120px]" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider mb-5 mx-auto">
              <i className="ri-scales-3-line"></i>
              Regulatory architecture
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl leading-tight">
              Compliance is not a feature — it is the foundation.
            </h2>
            <p className="mt-4 text-background-200 leading-relaxed">
              Built on the EU DLT Pilot Regime framework and MiCA-aligned architecture. Our compliance engine adapts to your jurisdiction with localized rule sets, automated reporting, and regulator-grade audit trails.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-global-line', title: 'Jurisdictional Rules Engine', desc: 'Automatically applies the correct KYC, AML, and transfer restriction rules based on each investor\'s jurisdiction and the asset\'s domicile. Covers 190+ countries with regular regulatory updates.' },
              { icon: 'ri-user-search-line', title: 'KYC / AML Automation', desc: 'Integrated with leading data providers for PEP screening, sanctions checks, adverse media, and identity verification. Ongoing monitoring flags changes in investor risk profiles in real time.' },
              { icon: 'ri-file-lock-line', title: 'Immutable Audit Trail', desc: 'Every action — from title deed registration to token transfer — is logged on-chain and off-chain. Exportable reports for regulators, auditors, and internal compliance teams at any granularity.' },
              { icon: 'ri-dashboard-line', title: 'Regulator Dashboard', desc: 'Dedicated read-only dashboards for regulators with real-time access to cap tables, transaction logs, KYC records, and distribution history — no manual reporting required.' },
              { icon: 'ri-bank-card-line', title: 'Fiat & Crypto Compliance', desc: 'Payment rails are integrated with licensed payment partners. All fiat and crypto transactions pass through the same KYC/AML checks before settlement.' },
              { icon: 'ri-shield-star-line', title: 'Smart Contract Audit', desc: 'Every token contract is audited by a top-tier third-party security firm before deployment. Audit reports are published and accessible to all investors.' },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-md bg-background-50/5 border border-background-50/10">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500/20 text-accent-300 mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <div className="text-base font-semibold mb-2">{item.title}</div>
                <p className="text-sm text-background-300 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry-form" className="py-20 md:py-28">
        <div className="max-w-[800px] mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
            <i className="ri-mail-send-line"></i>
            Get in touch
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
            Ready to tokenize your real estate assets?
          </h2>
          <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-lg mx-auto">
            Our RWA product team will walk you through the platform, answer regulatory questions, and build a timeline tailored to your asset.
          </p>

          <form
            data-readdy-form
            onSubmit={handleInquirySubmit}
            className="mt-10 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 text-left"
          >
            <input
              type="text"
              name="company_alt"
              className="field-alt-website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="rwa-first-name" className="block text-sm font-medium text-foreground-700 mb-1.5">First name *</label>
                <input
                  id="rwa-first-name"
                  name="first_name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="rwa-last-name" className="block text-sm font-medium text-foreground-700 mb-1.5">Last name *</label>
                <input
                  id="rwa-last-name"
                  name="last_name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="rwa-email" className="block text-sm font-medium text-foreground-700 mb-1.5">Work email *</label>
                <input
                  id="rwa-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label htmlFor="rwa-company" className="block text-sm font-medium text-foreground-700 mb-1.5">Company name *</label>
                <input
                  id="rwa-company"
                  name="company"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="Acme Capital"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="rwa-asset-type" className="block text-sm font-medium text-foreground-700 mb-1.5">Asset type of interest *</label>
              <select
                id="rwa-asset-type"
                name="asset_type"
                required
                className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors appearance-none cursor-pointer"
              >
                <option value="">Select asset type</option>
                <option value="residential">Residential Real Estate</option>
                <option value="commercial">Commercial Real Estate</option>
                <option value="industrial">Industrial / Logistics</option>
                <option value="land">Land / Development</option>
                <option value="portfolio">Multi-Asset Portfolio</option>
                <option value="other">Other Real-World Asset</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="rwa-value" className="block text-sm font-medium text-foreground-700 mb-1.5">Estimated asset value (USD) *</label>
              <select
                id="rwa-value"
                name="asset_value"
                required
                className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors appearance-none cursor-pointer"
              >
                <option value="">Select value range</option>
                <option value="1M-5M">$1M - $5M</option>
                <option value="5M-20M">$5M - $20M</option>
                <option value="20M-50M">$20M - $50M</option>
                <option value="50M-100M">$50M - $100M</option>
                <option value="100M+">$100M+</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="rwa-message" className="block text-sm font-medium text-foreground-700 mb-1.5">Tell us about your project (optional)</label>
              <textarea
                id="rwa-message"
                name="message"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                placeholder="Share details about the asset, jurisdiction, timeline, and any specific requirements..."
              ></textarea>
              <p className="text-xs text-foreground-500 mt-1">Max 500 characters</p>
            </div>

            {formStatus && (
              <div className={`mb-6 p-3 rounded-md text-sm font-medium ${
                formStatus.type === 'success'
                  ? 'bg-accent-100 border border-accent-300 text-accent-800'
                  : 'bg-red-50 border border-red-300 text-red-700'
              }`}>
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              className="w-full px-6 py-3.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              Submit inquiry
            </button>
          </form>

          {/* Direct Contact Block */}
          <div className="mt-10 max-w-lg mx-auto bg-background-100 border border-background-200 rounded-md p-5 text-center">
            <p className="text-sm text-foreground-600 mb-3">Prefer to reach out directly?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="mailto:tokenization@skyedge.am" className="flex items-center gap-2 text-foreground-700 hover:text-accent-600 cursor-pointer transition-colors">
                <i className="ri-mail-line text-accent-500"></i>
                tokenization@skyedge.am
              </a>
              <a href="tel:+37455432763" className="flex items-center gap-2 text-foreground-700 hover:text-accent-600 cursor-pointer transition-colors">
                <i className="ri-phone-line text-accent-500"></i>
                +374 55 432763
              </a>
              <a href="tel:+971529548257" className="flex items-center gap-2 text-foreground-700 hover:text-accent-600 cursor-pointer transition-colors">
                <i className="ri-phone-line text-accent-500"></i>
                +971 52 9548257
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}